import { Component, Input, ViewChild } from '@angular/core';
import { GlobalEventBusService, IDeviceContract, TicketService, XDeviceService } from '@nx-fe/services';
import { GlobalEvents } from 'sspapp/core/global-event-constants';
import { DeviceContextService } from 'sspapp/services/device-context.service';
import { SearchAndPickV2Component } from 'sspapp/shared/components/search-and-pick/search-and-pick.component';
import { TicketContextService } from '../../services/ticket-context.service';
import {
  IAssociatedDeviceModel,
  INewAssociatedDeviceModel,
  INewAssociatedManagedDeviceModel,
  Ticket,
} from '../../ticket.model';

@Component({
  selector: 'ssp-ticket-details-description',
  templateUrl: './ticket-details-description.component.html',
  styleUrls: ['./ticket-details-description.component.scss'],
})
export class TicketDetailsDescriptionComponent {
  @Input()
  public ticket: Ticket;
  @Input()
  public editable: boolean;

  @ViewChild(SearchAndPickV2Component, { static: false }) searchAndPickDevice: SearchAndPickV2Component<any>;

  public isChanged: boolean = false;

  private isManagedDeviceMode: boolean = false;

  constructor(
    public ticketService: TicketService,
    public deviceService: XDeviceService,
    public deviceContextService: DeviceContextService,
    public ticketContextService: TicketContextService,
    private globalEventBusService: GlobalEventBusService
  ) {
    this.ticket = this.ticketContextService.ticket as undefined as Ticket;
    this.ticketContextService.devicesToAssociate = [];

    this.globalEventBusService.register(GlobalEvents.validateTicket, () => {
      this.isChanged = true;
    });
  }

  /** Search resource for search and pick that searches in-memory devices from deviceContextService */
  public searchResource = {
    query: (params: { searchText: string; excludeIds: string }, success: Function) => {
      const listOfDevices: IDeviceContract[] = [];
      const maxResults = 5;
      const devicesSsp = this.deviceContextService.devicesSsp.filter(device => {
        return device && device.deviceName && device.deviceId;
      });
      const searchText = params.searchText.toLowerCase();

      if (params.searchText !== undefined && params.searchText.length > 0) {
        // Check for exact match (case insensitive)
        for (const deviceSpp of devicesSsp) {
          const deviceName = deviceSpp.deviceName.toLowerCase();
          const deviceId = deviceSpp.deviceId;

          if (deviceName.indexOf(searchText) === 0 && !this.getPendingAssociateManagedDevice(deviceId)) {
            listOfDevices.push(deviceSpp);
          }

          if (listOfDevices.length >= maxResults) {
            break;
          }
        }

        // Check for contain match (case insensitive)
        for (const deviceSpp of devicesSsp) {
          if (listOfDevices.length >= maxResults) {
            break;
          }

          const deviceName = deviceSpp.deviceName.toLowerCase();
          const deviceId = deviceSpp.deviceId;

          if (deviceName.indexOf(searchText) > 0 && !this.getPendingAssociateManagedDevice(deviceId)) {
            listOfDevices.push(deviceSpp);
          }
        }
      }
      success(listOfDevices);
    },
  };

  /**
   * This change is specifically for Windows Modern Desktop.
   * Since we are only have device family and kind.
   * The difference between Legacy Windows and modern Windows are:
   *                  Device Kind          | Device Family
   * Legacy Desktop | WindowsDesktopLegacy | WindowsDesktop
   * Modern Desktop | WindowsDesktop       | WindowsPhone
   * Therefore we need to matche those two here to determine the actual platform
   * TODO:
   *      Use one parameter to determine the platform instead of two.
   */
  public getDeviceIconClass = (deviceFamily: string, deviceKind: number): string => {
    if (deviceFamily === 'iOS') {
      deviceFamily = 'Apple';
    }
    /*if (deviceKind != null && (deviceKind === 8 && deviceFamily === 'WindowsPhone')) {
            deviceFamily = 'WindowsDesktop';
        }*/
    const deviceIconClass = this.deviceService.getDeviceIconClass(deviceFamily);

    return deviceIconClass;
  };

  public setManagedDeviceMode = (devicePanelToggle: boolean) => {
    this.isManagedDeviceMode = devicePanelToggle;
  };

  public addUnmanagedDevice = (deviceName?: string) => {
    if (!this.isManagedDeviceMode) {
      // Only working with non-existing device name
      if (deviceName && !this.getPendingAssociateUnmanagedDevice(deviceName)) {
        const unmanagedDevice = { deviceName: deviceName } as INewAssociatedDeviceModel;
        this.ticketContextService.devicesToAssociate.push(unmanagedDevice);
        this.searchAndPickDevice.clearSearch();
      }
    }
  };

  public getDeviceScreenIconClass = (deviceFamily: string, deviceKind: number, connectionName = undefined): string => {
    if (deviceFamily === 'iOS') {
      deviceFamily = 'Apple';
    }
    if (deviceKind != null && deviceKind === 8 && deviceFamily === 'WindowsPhone') {
      deviceFamily = 'WindowsDesktop';
    }
    let deviceScreenIconClass = this.deviceService.getDeviceScreenIconClass(deviceFamily, connectionName);

    return deviceScreenIconClass;
  };

  get ticketDescription(): string {
    return this.ticket ? this.ticket.description : '';
  }

  /** Sets the ticket description */
  set ticketDescription(value: string) {
    this.ticket.description = value;
    this.isChanged = true;
  }

  public setDirty() {
    this.isChanged = true;
  }

  public isValid() {
    return this.ticket && this.ticket.description && this.ticket.description.length > 0;
  }

  public addAssociatedDevice = (device: INewAssociatedManagedDeviceModel) => {
    //this.deviceIds.push(device.deviceId);
    this.ticketContextService.devicesToAssociate.push(device as INewAssociatedDeviceModel);
  };

  /**
   * Disassociates a device from the ticket.
   * @param deviceId The device to disassociate.
   */
  public disassociateDevice = (deviceForDisassociate: INewAssociatedDeviceModel): void => {
    console.log('Disassociating device from ticket');

    if (!this.editable) {
      console.log('Disassociate devices is not allowed');
      return;
    }

    this.ticketContextService.devicesToAssociate = this.ticketContextService.devicesToAssociate.filter(
      (device: INewAssociatedDeviceModel) => {
        if (
          deviceForDisassociate &&
          deviceForDisassociate.deviceName &&
          device &&
          device.deviceName &&
          deviceForDisassociate.deviceName.toLowerCase() == device.deviceName.toLowerCase()
        ) {
          return false;
        }

        if (
          deviceForDisassociate &&
          deviceForDisassociate.deviceId &&
          device &&
          device.deviceId &&
          deviceForDisassociate.deviceId == device.deviceId
        ) {
          return false;
        }

        return true;
      }
    );
  };

  public hasAssociatedDevices = (): boolean => {
    const devices = this.getAssociatedDevices();
    return devices && devices.length > 0;
  };

  /** Gets the current list of associated devices */
  public getAssociatedDevices = (): INewAssociatedDeviceModel[] | IAssociatedDeviceModel[] => {
    if (this.editable) {
      return this.ticketContextService.devicesToAssociate;
    }
    return this.ticketContextService.associatedDevices;
  };

  private getPendingAssociateUnmanagedDevice = (deviceName: string): INewAssociatedDeviceModel => {
    return this.ticketContextService.devicesToAssociate.find((device: INewAssociatedDeviceModel) => {
      if (deviceName && device && device.deviceName && device.deviceName.toLowerCase() == deviceName.toLowerCase())
        return true;
    });
  };

  private getPendingAssociateManagedDevice = (deviceId: string): INewAssociatedDeviceModel => {
    return this.ticketContextService.devicesToAssociate.find((device: INewAssociatedDeviceModel) => {
      if (deviceId && device && device.deviceId && device.deviceId == deviceId) return true;
    });
  };
}
